import { Volume2, Square } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { useTextToSpeech } from '@/hooks/useTextToSpeech';

export interface SpeakButtonProps {
  text: string;
  lang?: string;
  className?: string;
  ariaLabel?: string;
}

/**
 * Small icon button that reads `text` aloud using the browser's
 * speech synthesis. Renders nothing when the API is unsupported.
 */
export function SpeakButton({ text, lang = 'en-US', className, ariaLabel }: SpeakButtonProps) {
  const { supported, speaking, toggle } = useTextToSpeech({ lang });
  if (!supported) return null;

  return (
    <button
      type="button"
      onClick={() => toggle(text)}
      aria-pressed={speaking}
      aria-label={ariaLabel ?? (speaking ? 'Stop reading' : 'Read aloud')}
      title={speaking ? 'Stop reading' : 'Read aloud'}
      className={cn(
        'inline-flex items-center justify-center w-7 h-7 rounded-full',
        'text-[var(--color-text-secondary)] hover:text-[var(--color-accent-primary)]',
        'hover:bg-[var(--color-bg-tertiary)] transition-colors',
        speaking && 'text-[var(--color-accent-primary)] bg-[var(--color-bg-tertiary)]',
        className,
      )}
    >
      {speaking ? <Square className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
    </button>
  );
}
